import React, { useCallback, useMemo, useRef, useState } from 'react';
import { DragOutlined, FormOutlined, DeleteOutlined, PlusSquareOutlined, UploadOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { Button, Col, Row } from 'antd';
import { ButtonLoadMoreItems } from './ButtonLoadMoreItems';
import { ModalEditNode } from './ModalEditNode';
import { NodeSlot } from './NodeSlot';
import { useListNodes } from './hooks/useListNodes';
import { ChildNodesWrapper, ContentItem, ItemsCrud, NodeName } from './style';
import { useNotifications } from '../../../../hooks/useNotifications';
import { useDictionaryTranslation } from '../../../../hooks/useDictionaryTranslation';
import { debounceFunction } from '../../../../utility/utility';
import { useImportCustomList } from '../customLists/hooks/useImportCustomList';
import ModalImportItem from '../../../../components/importsModal/ModalImportItems';

const CustomListNode = ({ path, nodeData, renderChildNodes, nodeIndex, customListId }) => {
  const dndType = 'customListNode';
  const { showConfirmNotification } = useNotifications();
  const { translate: t } = useDictionaryTranslation();
  const listNodesData = useListNodes();
  const { importList } = useImportCustomList();
  const nodeRef = useRef();
  const nodeContainerRef = useRef();
  const [modalEditNodeData, setModalEditNodeData] = useState({
    show: false,
    nodeData: null,
    actionHandler: null,
  });
  const [isOpenImportModal, setOpenImportModal] = useState(false);
  const hasDescription = useMemo(
    () => nodeData.description?.trim?.().length > 0,
    [nodeData.description],
  );

  const toggleChildNodesHandlerRef = useRef();
  toggleChildNodesHandlerRef.current = () => {
    const value = !nodeData.showChildNodes;
    listNodesData.updateNodeData({
      nodeId: nodeData.id,
      path,
      nodeData: { showChildNodes: value },
    });
    if (value) {
      listNodesData.getListNodesPaginated({ nodeId: nodeData.id, path, page: 1 });
    }
  };

  const toggleChildNodes = useRef(debounceFunction(() => toggleChildNodesHandlerRef.current(), 250)).current;

  const closeNodesModal = useCallback(() => {
    setModalEditNodeData({ show: false });
  }, []);

  const onDeleteNodeList = useCallback(() => {
    showConfirmNotification({
      title: t('areYouSure'),
      content: t('recordWillBeRemoved', { recordDescription: nodeData.title }),
      onOk: async () => {
        await listNodesData.deleteListNode({ path, nodeId: nodeData.id });
      },
    });
  }, [showConfirmNotification, t, nodeData.title, nodeData.id, path, listNodesData]);

  const onEditCurrentNode = useCallback(() => {
    setModalEditNodeData({
      show: true,
      nodeData,
      actionHandler: async (nodeId, values) => {
        listNodesData.updateListNode({ path, nodeId, nodeData: values });
      },
    });
  }, [nodeData, path, listNodesData]);

  const handleAddNode = useCallback(() => {
    setModalEditNodeData({
      show: true,
      nodeData: null,
      actionHandler: async (_, values) => {
        listNodesData.addListNode({
          path: [...path, nodeData.id],
          nodeData: { ...values, id: String(new Date().getTime()) },
        });
      },
    });
  }, [path, nodeData.id, listNodesData]);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
  }, []);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    if (listNodesData.dnd.draggedNode?.id === nodeData.id) return;
    if (nodeContainerRef.current !== event.target && nodeRef.current !== event.target) return;
    const isBefore = event.nativeEvent.offsetY < event.target.clientHeight / 2;
    const targetIndex = isBefore ? nodeIndex : nodeIndex + 1;
    listNodesData.dnd.updateDropTarget({
      targetIndex,
      targetPath: path,
      anchorNode: nodeData.id,
    });
  }, [listNodesData, nodeData.id, nodeIndex, path]);

  const handleDragStart = useCallback((event) => {
    event.dataTransfer.setData(
      'text/plain',
      JSON.stringify({
        type: dndType,
        data: { id: nodeData.id, path },
      }),
    );
    listNodesData.dnd.startDragging({
      draggedNode: {
        id: nodeData.id,
        title: nodeData.title,
        description: nodeData.description,
        path,
        nodeIndex,
      },
    });
  }, [dndType, nodeData.id, nodeData.title, nodeData.description, path, nodeIndex, listNodesData]);

  const handleDragEnd = useCallback((event) => {
    event.preventDefault();
    listNodesData.dnd.stopDragging();
  }, [listNodesData]);

  const openImportModal = useCallback(() => {
    setOpenImportModal(true);
  }, []);

  const closeImportModal = useCallback(({ successImport = false } = {}) => {
    if (successImport) {
      listNodesData.getListNodesPaginated({ nodeId: nodeData.id, path, page: 1 });
    }
    setOpenImportModal(false);
  }, [listNodesData, nodeData.id, path]);

  const onHandleImportChildList = useCallback(async (data) => {
    const parentsIds = [...path, nodeData.id];
    await importList(customListId, data.file, data.idAcount, parentsIds);
  }, [importList, customListId, path, nodeData.id]);

  return (
    <>
      <div
        id={nodeData.id}
        ref={nodeContainerRef}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
      >
        {listNodesData.dnd.anchorNode === nodeData.id && nodeIndex === 0 && listNodesData.dnd.targetIndex === 0 ? (
          <NodeSlot path={path} nodeData={listNodesData.dnd.draggedNode} />
        ) : null}

        <ContentItem ref={nodeRef} draggable onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <Row>
            <Col md={14}>
              {nodeData.hasChildren ? (
                <Button
                  onClick={toggleChildNodes}
                  icon={<DragOutlined style={{ fontSize: '12px' }} className="iconDrag" />}
                />
              ) : null}
              <NodeName>{nodeData.title}</NodeName>
            </Col>
            <Col md={hasDescription ? 24 : 7}>
              <p>{nodeData.description}</p>
            </Col>

            <Col md={hasDescription ? 24 : 3}>
              <Row justify="end">
                <ItemsCrud>
                  <Button type="secondary" onClick={handleAddNode} icon={<PlusSquareOutlined />} />
                  <Button type="secondary" onClick={openImportModal} icon={<UploadOutlined />} />
                  <Button type="secondary" onClick={onEditCurrentNode} icon={<FormOutlined />} />
                  <Button type="secondary" onClick={onDeleteNodeList} icon={<DeleteOutlined />} />
                </ItemsCrud>
              </Row>
            </Col>
          </Row>
        </ContentItem>
        {nodeData.showChildNodes ? (
          <>
            <ChildNodesWrapper>
              {renderChildNodes(nodeData.nodes)}
              <ButtonLoadMoreItems
                path={path}
                nodeId={nodeData.id}
                page={nodeData.page}
                pageSize={nodeData.pageSize}
                total={nodeData.total}
              />
            </ChildNodesWrapper>
          </>
        ) : null}
        {listNodesData.dnd.anchorNode === nodeData.id && listNodesData.dnd.targetIndex === nodeIndex + 1 ? (
          <NodeSlot path={path} nodeData={listNodesData.dnd.draggedNode} />
        ) : null}
      </div>

      {modalEditNodeData.show ? (
        <ModalEditNode
          show={modalEditNodeData.show}
          handleClose={closeNodesModal}
          nodeData={modalEditNodeData.nodeData}
          saveNode={modalEditNodeData.actionHandler}
        />
      ) : null}
      <ModalImportItem
        handleClose={closeImportModal}
        show={isOpenImportModal}
        importList={onHandleImportChildList}
      />
    </>
  );
};

CustomListNode.propTypes = {
  nodeData: PropTypes.object,
  renderChildNodes: PropTypes.func,
  path: PropTypes.array,
  nodeIndex: PropTypes.number,
  customListId: PropTypes.string,
};

export default React.memo(CustomListNode);
